let UUID = 0;

/**
 * <manual-def> — inline defined term with a tooltip (the [def] shortcode).
 *
 *  - Hover / keyboard focus reveals the definition; leaving hides it.
 *  - Click/tap pins it open (touch has no hover), click-away or Escape unpins.
 *  - The tip is centered over the term, then nudged horizontally (--def-shift)
 *    so it never leaves the viewport.
 */
export default class ManualDef extends HTMLElement {
  connectedCallback() {
    this.$term = this.querySelector('[data-def-term]');
    this.$tip = this.querySelector('[data-def-tip]');
    if (!this.$term || !this.$tip) return;

    const id = 'manual-def-' + (UUID++);
    this.$tip.setAttribute('id', id);
    this.$term.setAttribute('aria-describedby', id);

    this._pinned = false;

    this._place = () => {
      this.$tip.style.setProperty('--def-shift', '0px');
      const r = this.$tip.getBoundingClientRect();
      const pad = 16;
      let shift = 0;
      if (r.left < pad) shift = pad - r.left;
      else if (r.right > window.innerWidth - pad) shift = (window.innerWidth - pad) - r.right;
      if (shift) this.$tip.style.setProperty('--def-shift', shift + 'px');
    };
    this._show = () => {
      this.classList.add('is-open');
      this.$term.setAttribute('aria-expanded', 'true');
      this._place();
    };
    this._hide = (force) => {
      if (this._pinned && !force) return;
      this.classList.remove('is-open');
      this.$term.setAttribute('aria-expanded', 'false');
    };

    this._onEnter = () => this._show();
    this._onLeave = () => this._hide(false);
    this._onClick = (e) => {
      e.preventDefault();
      this._pinned = !this._pinned;
      if (this._pinned) this._show();
      else this._hide(true);
    };
    this._onDocClick = (e) => {
      if (this._pinned && !this.contains(e.target)) { this._pinned = false; this._hide(true); }
    };
    this._onKey = (e) => {
      if (e.key === 'Escape') { this._pinned = false; this._hide(true); }
    };

    this.addEventListener('mouseenter', this._onEnter);
    this.addEventListener('mouseleave', this._onLeave);
    this.$term.addEventListener('focus', this._onEnter);
    this.$term.addEventListener('blur', this._onLeave);
    this.$term.addEventListener('click', this._onClick);
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this._onEnter);
    this.removeEventListener('mouseleave', this._onLeave);
    this.$term?.removeEventListener('focus', this._onEnter);
    this.$term?.removeEventListener('blur', this._onLeave);
    this.$term?.removeEventListener('click', this._onClick);
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
  }
}

customElements.define('manual-def', ManualDef);
